import { useQuery } from '@apollo/client';
import { createError, MonthYearPeriod } from '@common/neo.tax';
import { useEffect } from 'react';

import {
  AnalysisType,
  GET_UNFINISHED_SYNC_EVENTS_IN_PERIOD_BY_ANALYSIS_TYPE,
  GetUnfinishedSyncEventsInPeriodByAnalysisTypeQuery,
  GetUnfinishedSyncEventsInPeriodByAnalysisTypeQueryVariables,
  PeriodName,
} from '../graphql';

const POLL_INTERVAL_MS = 10000;

export type UseUnfinishedSyncEventInPeriodByAnalysisType =
  | {
      period: MonthYearPeriod | null;
      loading: false;
    }
  | {
      period: null;
      loading: true;
    };

export const useUnfinishedSyncEventInPeriodByAnalysisType = ({
  analysisType,
  fiscalYearId,
  periodName,
}: {
  fiscalYearId: string;
  periodName: PeriodName;
  analysisType: AnalysisType;
}): UseUnfinishedSyncEventInPeriodByAnalysisType => {
  const { data, loading, startPolling, stopPolling } = useQuery<
    GetUnfinishedSyncEventsInPeriodByAnalysisTypeQuery,
    GetUnfinishedSyncEventsInPeriodByAnalysisTypeQueryVariables
  >(GET_UNFINISHED_SYNC_EVENTS_IN_PERIOD_BY_ANALYSIS_TYPE, {
    variables: {
      fiscalYearId,
      periodName,
      analysisType,
    },
    pollInterval: 0, // Start with no polling
  });

  // Start polling when there's an ongoing sync, stop when there isn't
  useEffect(() => {
    if (data?.getUnfinishedSyncEventsInPeriodByAnalysisType) {
      startPolling(POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return (): void => stopPolling();
  }, [data?.getUnfinishedSyncEventsInPeriodByAnalysisType, startPolling, stopPolling]);

  if (loading) {
    return { period: null, loading: true };
  }

  if (!data) {
    throw createError('No data returned from useUnfinishedSyncEventInPeriodByAnalysisType');
  }

  return {
    period: data.getUnfinishedSyncEventsInPeriodByAnalysisType ?? null,
    loading: false,
  };
};
